// src/shared/components/FramerAnimations.js
import React from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { useRef } from 'react';

// Basic blur-in animation
export const BlurIn = ({
  children,
  className = '',
  delay = 0,
  duration = 0.8,
  blurAmount = 15,
  y = 50,
}) => {
  return (
    <motion.div
      className={className}
      initial={{
        filter: `blur(${blurAmount}px)`,
        opacity: 0,
        y: y,
      }}
      animate={{
        filter: 'blur(0px)',
        opacity: 1,
        y: 0,
      }}
      transition={{
        duration: duration,
        delay: delay,
        ease: [0.4, 0, 0.2, 1],
      }}
    >
      {children}
    </motion.div>
  );
};

// Scroll-triggered blur in
export const ScrollBlurIn = ({
  children,
  className = '',
  blurAmount = 12,
  y = 60,
  triggerOnce = true,
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: triggerOnce,
    margin: '-100px',
  });

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{
        filter: `blur(${blurAmount}px)`,
        opacity: 0,
        y: y,
      }}
      animate={
        isInView
          ? {
              filter: 'blur(0px)',
              opacity: 1,
              y: 0,
            }
          : {}
      }
      transition={{
        duration: 0.8,
        ease: [0.25, 0.46, 0.45, 0.94],
      }}
    >
      {children}
    </motion.div>
  );
};

// Staggered blur animations
export const StaggeredBlurIn = ({
  children,
  className = '',
  staggerDelay = 0.1,
  childClassName = '',
}) => {
  const containerVariants = {
    hidden: { opacity: 1 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: staggerDelay,
      },
    },
  };

  const itemVariants = {
    hidden: {
      filter: 'blur(15px)',
      opacity: 0,
      y: 50,
    },
    visible: {
      filter: 'blur(0px)',
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.4, 0, 0.2, 1],
      },
    },
  };

  return (
    <motion.div
      className={className}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {React.Children.map(children, (child, index) => (
        <motion.div
          key={index}
          className={childClassName}
          variants={itemVariants}
        >
          {child}
        </motion.div>
      ))}
    </motion.div>
  );
};

// Scroll-triggered staggered animations
export const ScrollStaggered = ({
  children,
  className = '',
  staggerDelay = 0.15,
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px',
  });

  const containerVariants = {
    hidden: { opacity: 1 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: staggerDelay,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: {
      filter: 'blur(12px)',
      opacity: 0,
      y: 40,
      scale: 0.95,
    },
    visible: {
      filter: 'blur(0px)',
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.7,
        ease: [0.34, 1.56, 0.64, 1],
      },
    },
  };

  return (
    <motion.div
      ref={ref}
      className={className}
      variants={containerVariants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
    >
      {React.Children.map(children, (child, index) => (
        <motion.div key={index} variants={itemVariants}>
          {child}
        </motion.div>
      ))}
    </motion.div>
  );
};

// Parallax blur effect with scroll
export const ParallaxBlur = ({
  children,
  className = '',
  blurRange = [0, 10],
  yRange = [0, -50],
  opacityRange = [1, 0.3],
}) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });

  const blur = useTransform(scrollYProgress, [0, 1], blurRange);
  const y = useTransform(scrollYProgress, [0, 1], yRange);
  const opacity = useTransform(scrollYProgress, [0, 1], opacityRange);

  return (
    <motion.div
      ref={ref}
      className={className}
      style={{
        filter: blur,
        y: y,
        opacity: opacity,
      }}
    >
      {children}
    </motion.div>
  );
};

// Advanced card animation with hover
export const AnimatedCard = ({
  children,
  className = '',
  hoverScale = 1.05,
  delay = 0,
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{
        filter: 'blur(15px)',
        opacity: 0,
        y: 60,
        scale: 0.9,
      }}
      animate={
        isInView
          ? {
              filter: 'blur(0px)',
              opacity: 1,
              y: 0,
              scale: 1,
            }
          : {}
      }
      whileHover={{
        scale: hoverScale,
        y: -5,
        transition: { duration: 0.2 },
      }}
      whileTap={{ scale: 0.98 }}
      transition={{
        duration: 0.8,
        delay: delay,
        ease: [0.25, 0.46, 0.45, 0.94],
      }}
    >
      {children}
    </motion.div>
  );
};

// Text reveal animation
export const TextReveal = ({
  text,
  className = '',
  splitBy = 'word',
  staggerDelay = 0.08,
}) => {
  const words = splitBy === 'word' ? text.split(' ') : text.split('');

  const containerVariants = {
    hidden: { opacity: 1 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: staggerDelay,
      },
    },
  };

  const childVariants = {
    hidden: {
      filter: 'blur(8px)',
      opacity: 0,
      y: 20,
    },
    visible: {
      filter: 'blur(0px)',
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.4, 0, 0.2, 1],
      },
    },
  };

  return (
    <motion.div
      className={className}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {words.map((word, index) => (
        <motion.span
          key={index}
          className={splitBy === 'word' ? 'inline-block mr-2' : 'inline-block'}
          variants={childVariants}
        >
          {word}
          {splitBy === 'word' && ' '}
        </motion.span>
      ))}
    </motion.div>
  );
};

// Page transition wrapper
export const PageTransition = ({ children, className = '' }) => {
  return (
    <motion.div
      className={className}
      initial={{ filter: 'blur(20px)', opacity: 0 }}
      animate={{ filter: 'blur(0px)', opacity: 1 }}
      exit={{ filter: 'blur(20px)', opacity: 0 }}
      transition={{
        duration: 0.5,
        ease: [0.4, 0, 0.2, 1],
      }}
    >
      {children}
    </motion.div>
  );
};

// Scroll progress blur
export const ScrollProgressBlur = ({ children, className = '' }) => {
  const { scrollYProgress } = useScroll();
  const blur = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [15, 0, 0, 5]);
  const scale = useTransform(
    scrollYProgress,
    [0, 0.2, 0.8, 1],
    [0.9, 1, 1, 0.95]
  );

  return (
    <motion.div
      className={className}
      style={{
        filter: blur,
        scale: scale,
      }}
    >
      {children}
    </motion.div>
  );
};
